import styled, { css } from 'styled-components';

export const ModalBody = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  font-family: 'Plus Jakarta Sans', sans-serif;
`;

export const SectionLabel = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  font-weight: 700;
  color: #006633;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  padding-bottom: 4px;
  border-bottom: 1px solid #eef2f7;
`;

export const FieldGroup = styled.div<{ $cols?: number }>`
  display: grid;
  grid-template-columns: repeat(${({ $cols = 2 }) => $cols}, minmax(0, 1fr));
  gap: 12px;

  @media (max-width: 576px) {
    grid-template-columns: 1fr;
  }
`;

export const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;

  label {
    display: flex;
    flex-direction: column;
    gap: 6px;
    font-size: 11px;
    font-weight: 700;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  input,
  select,
  textarea {
    padding: 0 12px;
    border: 1px solid #e2e8f0;
    border-radius: 10px;
    font-size: 14px;
    font-family: inherit;
    font-weight: 400;
    color: #0f172a;
    background: #ffffff;
    text-transform: none;
    letter-spacing: normal;
    outline: none;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;

    &:hover {
      border-color: #cbd5e1;
    }

    &:focus {
      border-color: #006633;
      box-shadow: 0 0 0 3px rgba(0, 102, 51, 0.12);
    }

    &::placeholder {
      color: #94a3b8;
    }

    &:disabled {
      background: #f8fafc;
      color: #94a3b8;
      cursor: not-allowed;
    }
  }

  input,
  select {
    height: 40px;
  }

  textarea {
    padding-top: 10px;
    padding-bottom: 10px;
    resize: vertical;
    min-height: 90px;
    line-height: 1.5;
  }

  .error {
    color: #b91c1c;
    font-size: 12px;
    font-weight: 500;
  }
`;

export const AvatarPicker = styled.label<{ $hasImage?: boolean }>`
  display: flex;
  gap: 14px;
  align-items: center;
  padding: 14px;
  border: 1px dashed ${({ $hasImage }) => ($hasImage ? '#c7e4d5' : '#e2e8f0')};
  background: ${({ $hasImage }) => ($hasImage ? '#f7fcf9' : '#fafbfc')};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.15s ease;
  position: relative;

  &:hover {
    border-color: #006633;
    background: #f7fcf9;
  }

  .thumb {
    width: 72px;
    height: 72px;
    border-radius: 12px;
    overflow: hidden;
    background: #ffffff;
    border: 1px solid #eef2f7;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .thumb img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    background: #ffffff;
  }

  .placeholder {
    width: 72px;
    height: 72px;
    border-radius: 12px;
    background: #e6f3ec;
    color: #006633;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .info {
    flex: 1;
    min-width: 0;
  }

  .title {
    font-size: 13px;
    font-weight: 700;
    color: #0f172a;
  }

  .hint {
    font-size: 11.5px;
    color: #64748b;
    font-weight: 500;
    margin-top: 2px;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .action {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    font-size: 11px;
    color: #006633;
    font-weight: 700;
    margin-top: 6px;
    text-transform: uppercase;
    letter-spacing: 0.04em;
  }

  input[type='file'] {
    display: none;
  }
`;

export const RemoveAvatarBtn = styled.button`
  position: absolute;
  top: 8px;
  right: 8px;
  width: 26px;
  height: 26px;
  border-radius: 8px;
  background: #ffffff;
  color: #b91c1c;
  border: 1px solid #fecaca;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: inherit;

  &:hover {
    background: #fef2f2;
  }
`;

type BtnVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
const BTN_MAP: Record<
  BtnVariant,
  {
    bg: string;
    color: string;
    border: string;
    hoverBg: string;
    hoverBorder?: string;
  }
> = {
  primary: {
    bg: '#006633',
    color: '#ffffff',
    border: '#006633',
    hoverBg: '#00451f',
  },
  secondary: {
    bg: '#ffffff',
    color: '#0f172a',
    border: '#e2e8f0',
    hoverBg: '#f8fafc',
    hoverBorder: '#006633',
  },
  ghost: {
    bg: 'transparent',
    color: '#64748b',
    border: 'transparent',
    hoverBg: '#f1f5f9',
  },
  danger: {
    bg: '#ffffff',
    color: '#b91c1c',
    border: '#fecaca',
    hoverBg: '#fef2f2',
  },
};

export const Button = styled.button<{ $variant?: BtnVariant }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 38px;
  padding: 0 16px;
  border-radius: 10px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  font-family: inherit;

  ${({ $variant = 'primary' }) => css`
    background: ${BTN_MAP[$variant].bg};
    color: ${BTN_MAP[$variant].color};
    border: 1px solid ${BTN_MAP[$variant].border};

    &:hover:not(:disabled) {
      background: ${BTN_MAP[$variant].hoverBg};
      ${BTN_MAP[$variant].hoverBorder &&
      css`
        border-color: ${BTN_MAP[$variant].hoverBorder};
        color: #006633;
      `}
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

/* Legacy alias (kept for backwards compatibility if other files still import) */
export const PreviewImage = styled.img`
  background: white;
  padding: 8px;
  border-radius: 8px;
  max-width: 100%;
`;
